import { DefaultGameState, GameState, ItemDetail } from "@shared/types";
import { Page } from "../enums/Page";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { InventoryService } from "../services/InventoryServers";

const styles: string = css`
  .bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover; 
    z-index: -1; 
  }

  :host {
    font-family: "Onesize", serif;
    color: #2f1e0e;
    width: 100%;
    max-width: 1024px;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    box-sizing: border-box;
  }

  .title {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: bold;
    text-shadow: 1px 1px 2px #fff8e1;
  }

  .content {
    flex: 10;
    display: flex;
    gap: 1rem;
  }

  .item {
    flex: 2;
    background-color: rgba(255, 255, 255, 0.5);
    border: 2px solid #6b4c2e;
    border-radius: 8px;
    padding: 1rem;
    overflow-y: auto;
  }

  .item li {
    list-style: none;
    margin-bottom: 0.5rem;
  }

  .item a.button {
    display: inline-block;
    padding: 0.5rem 1rem;
    background-color: #d9c2a3;
    border: 1px solid #6b4c2e;
    border-radius: 4px;
    text-decoration: none;
    color: #2f1e0e;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 18px;
  }

  .item a.button:hover {
    background-color: #c4a97c;
  }

  .info {
    flex: 5;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    background-color: rgba(255, 255, 255, 0.5);
    border: 2px solid #6b4c2e;
    border-radius: 8px;
    padding: 1rem;
  }

  .img {
    flex: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .img img {
    max-width: 300px;
    max-height: 300px;
    object-fit: contain;
  }

  .text {
    flex: 2;
    font-size: 20px;
    line-height: 1.4;
  }

  .close {
    flex: 0;
    text-align: right;
    margin-top: 1rem;
  }

  .close button {
    background-color: #a05e2b;
    color: #fff;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
  }

  .close button:hover {
    background-color: #7f4622;
  }
`;

export class InventoryComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private readonly _gameInventoryService: InventoryService = new InventoryService();
    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current Inventory */
    private _currentInventory?: ItemDetail[] | undefined;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshGameState();
        this.shadowRoot?.addEventListener("click", event => {
            if ((event.target as HTMLElement).id === "close") {
                this._gameEventService.switchPage(Page.Canvas);
            }
        });
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        this.updateGameState(state);
    }

    /**
     * Update the inventory to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        // if (!this.shadowRoot) return;

        const elements: HTMLElement[] = htmlArray`
            <style>
            ${styles}           
            </style>
            <img src="/assets/img/rooms/background.png" class="bg">
            ${this.renderTitle()}
            ${this.renderContent()}
            ${this.renderCloseButton()}
        `;
        if (this.shadowRoot) {
            while (this.shadowRoot.firstChild) {
                this.shadowRoot.firstChild.remove();
            }

            this.shadowRoot.append(...elements);
        }
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        return "<div class= 'title'>Inventory</div>";
    }

    /**
     * Render the content element
     *
     * @returns HTML element of the content
     */
    private renderContent(): HTMLElement {
        return html`    <div class="content">
        <div class="item">
            <!-- item name  -->
            ${this._currentGameState?.Inventory.map(item => this.renderItems(item))}
        </div>
        <div class="info">
           <div class="img">${this.renderimg()}</div>
            <div class="text">${this.rendertext()}</div> 
        </div>
    </div>`;
    }

    // render name of items
    private renderItems(item: string): HTMLElement {
        const element: HTMLElement = html`
        <li>
            <a class="button">
                ${item}
            </a>
        </li>    
        `;
        element.addEventListener("click", () => this.handleClickItem(item));

        return element;
    }

    // private renderimg(): HTMLElement {
    //     const element: HTMLElement = html`
    //     `;

    //     return element;
    // }

    // render description of items
    private rendertext(): string {
        if (!this._currentInventory) {
            return "";
        }
        return `
            <div>
                ${this._currentInventory[0].detail}
            </div>
        `;
    }

    // render description of items
    private renderimg(): string {
        if (!this._currentInventory) {
            return "";
        }
        return `
            <div>
                <img src="/assets/img/items/${this._currentInventory[0].img}.png" alt="Items">
            </div>
        `;
    }

    // close the inventory
    private renderCloseButton(): string {
        return `<div class="close"> 
        <button id="close">Close</button>
        </div>`;
    }

    /**
 * Display image and details of item, if the user de name of item click
 * @param item Game item that was clicked
 */
    private async handleClickItem(item: string): Promise<void> {
        const itemDetail: ItemDetail[] | undefined = await this._gameInventoryService.itemDetails(item);
        this._currentInventory = itemDetail;
        this.render();
    }
}
